import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { FormField } from 'src/components';
import { TODAY_DATE } from 'src/components/AddComplimentaryPeriodModal/const';
import { PartnerGroupRules, usePartnerGroupRules } from 'src/graphql';
import { Modals, useModalState } from 'src/store/modals';
import { validateDateInput } from 'src/utils/date';
import { WARRANTY_LENGTH_OPTIONS } from './util';

export interface ProposalWonFormValues {
  warrantyLength: { id: string; label: string; value: number };
  estimatedDay1Date?: Dayjs | null;
  sendEducationalEmails: boolean;
  tosAccepted: boolean;
  hasPreSoldMembership: boolean;
  orgNotes?: string;
  membershipSalesNotes?: string;
}

export function useProposalWonForm() {
  const { modalProps } = useModalState(Modals.ProposalWon);
  const { partnerGroupRules } = usePartnerGroupRules(modalProps?.address?.partnerId);

  const hasAccessToPSM = partnerGroupRules[PartnerGroupRules.AccessToPresoldMemberships];
  const hasAccessToSubManagementPlus =
    partnerGroupRules[PartnerGroupRules.AccessToSubscriptionManagementPlus];

  const alreadyHasPreSoldMembership = useMemo(() => {
    const psm = modalProps?.address?.preSoldMemberships?.[0];
    return Boolean(psm?.id);
  }, [modalProps?.address?.preSoldMemberships]);

  const proposalWonFormFields = useMemo<FormField[]>(() => {
    return [
      {
        type: 'autocomplete' as const,
        name: 'warrantyLength',
        'data-testid': 'proposal-won-warranty',
        label: 'Warranty Length',
        options: WARRANTY_LENGTH_OPTIONS,
        defaultValue: WARRANTY_LENGTH_OPTIONS[0],
      },
      {
        type: 'datepicker' as const,
        name: 'estimatedDay1Date',
        'data-testid': 'proposal-won-estimated-day1',
        label: 'Estimated Day1 Date',
        value: null,
        defaultValue: dayjs(modalProps?.project?.estimatedDay1Date || TODAY_DATE),
        required: 'Estimated Day1 Date is required',
        rules: { validate: validateDateInput },
      },
      {
        type: 'switch' as const,
        name: 'tosAccepted',
        'data-testid': 'proposal-won-tos',
        label: 'Terms of Service Accepted',
        defaultValue: modalProps?.address?.tosAccepted === 'Yes',
      },
      {
        type: 'switch' as const,
        name: 'sendEducationalEmails',
        'data-testid': 'proposal-won-educational-emails',
        label: 'Send Educational Emails',
        defaultValue: false,
        hidden: !hasAccessToSubManagementPlus,
      },
      {
        type: 'switch' as const,
        name: 'hasPreSoldMembership',
        'data-testid': 'proposal-won-pre-sell',
        label: 'Did you "Pre-Sell" a membership?',
        defaultValue: false,
        hidden: !hasAccessToPSM || alreadyHasPreSoldMembership,
      },
      {
        type: 'rich-text-bubble' as const,
        name: 'orgNotes',
        'data-testid': 'proposal-won-site-notes',
        label: 'Site Notes',
        defaultValue: modalProps?.address?.orgNotes || '',
      },
      {
        type: 'rich-text-bubble' as const,
        name: 'membershipSalesNotes',
        'data-testid': 'proposal-won-sales-notes',
        label: 'Membership Sales Notes',
        defaultValue: '',
      },
    ];
  }, [
    alreadyHasPreSoldMembership,
    hasAccessToPSM,
    hasAccessToSubManagementPlus,
    modalProps?.address?.orgNotes,
    modalProps?.address?.tosAccepted,
    modalProps?.project?.estimatedDay1Date,
  ]);

  return { proposalWonFormFields };
}
